// One-shot signal that the user just authenticated. PortalEntry marks this
// immediately before the redirect; EntryFlowProvider (root) owns it from
// there and clears it once the door animation has actually played.
//
// Why the owner moved from "consume on read" to peek + explicit clear
// (2026-08-21): on a first run the user is routed to /onboarding before any
// shell mounts, so the flag has to survive the whole slide sequence. Reading
// it destructively from whichever component happened to mount first meant the
// door animation was spent on a route that never renders it.
//
// The event exists because the login-to-dashboard hop is a client navigation:
// the root layout does not remount, so a mount-time read alone would never
// see a flag written after it. sessionStorage covers the reload and the OAuth
// round-trip; the event covers the in-page transition.

const KEY = 'portal:justLoggedIn';

/** Fired by markJustLoggedIn so a already-mounted provider learns about it. */
export const LOGIN_EVENT = 'portal:login';

export function markJustLoggedIn(): void {
    try {
        sessionStorage.setItem(KEY, '1');
    } catch {
        // sessionStorage blocked (private mode, storage full): silently skip.
    }
    try {
        window.dispatchEvent(new Event(LOGIN_EVENT));
    } catch {
        // Non-browser context: nothing to notify.
    }
}

/** Read without clearing. Safe to call from any number of mounts. */
export function peekJustLoggedIn(): boolean {
    if (typeof window === 'undefined') return false;
    try {
        return sessionStorage.getItem(KEY) === '1';
    } catch {
        return false;
    }
}

/** Clear the flag. Called once the entry animation has been spent. */
export function clearJustLoggedIn(): void {
    if (typeof window === 'undefined') return;
    try {
        sessionStorage.removeItem(KEY);
    } catch {
        // sessionStorage blocked: nothing to clear.
    }
}
